import { z } from "zod";
import { DateTimeWithOffset, FalsyBool, NullishDateTimeWithOffset } from "@/lib/api/schemas/patterns";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";
import { CasePayload } from "@/lib/api/schemas/case";
import { AppResetMode } from "@/lib/api/schemas/suite";
import { ProjectType } from "@/lib/domain/platform";
import { RatioValue, UsageStatisticsSchema } from "@/lib/api/schemas/shared";

const StreamDataSchema = z.object({
  livekit_url: z.string(),
  room_name: z.string(),
  identity: z.string(),
  token: z.string(),
});

/**
 * A batch has no state column of its own: the backend derives this from the milestones it
 * stamps and from its cycles. The four are exclusive and cover every batch, and `queued` is
 * the same set as `is_pending=true`.
 */
const BatchState = z.enum(["queued", "running", "completed", "cancelled"]);

/** A batch's cap on the worker's agent; `null` leaves the worker's own default. */
const StepLimitIn = z.number().int().min(1).max(1000).nullable();

/**
 * Every accepted `ordering` value, each field in both directions (`-` sorts descending).
 * Spelled out rather than left a `string` because the backend answers an unknown field with a
 * 422: a typo belongs at the call site, not in a toast. `state` follows the lifecycle above
 * rather than the alphabet; `pass_rate` is the row's `stats.ratio.value`.
 */
const ScheduleListOrdering = z.enum([
  "id",
  "-id",
  "created_at",
  "-created_at",
  "updated_at",
  "-updated_at",
  "schedule_time",
  "-schedule_time",
  "invocation_time",
  "-invocation_time",
  "last_run",
  "-last_run",
  "worker_name",
  "-worker_name",
  "case_name",
  "-case_name",
  "suite_name",
  "-suite_name",
  "state",
  "-state",
  "pass_rate",
  "-pass_rate",
]);

/**
 * `ScheduleListFilters` in the contract — the batch list and every dashboard aggregate take
 * the same set. Omitting `project_ids` means "every project the client can see", which is
 * what makes the analytics and schedules pages global.
 *
 * A batch row is one *occurrence*, not a plan: a recurring schedule dispatches and
 * immediately creates its successor (`parent_schedule_id`). Hence `is_pending` ("not
 * dispatched yet") and `is_latest_in_chain` ("where this chain currently stands").
 */
const ScheduleListFilters = z.object({
  project_ids: z.number().array().optional(),
  worker_ids: z.number().array().optional(),
  schedule_ids: z.number().array().optional(),
  test_case_ids: z.number().array().optional(),
  test_suite_ids: z.number().array().optional(),
  artifact_ids: z.number().array().optional(),
  platform: ProjectType.array().optional(),
  /** Repeat the parameter for several: `states=queued&states=running`. */
  states: BatchState.array().optional(),
  created_at_from: z.union([z.string(), z.date()]).optional(),
  created_at_to: z.union([z.string(), z.date()]).optional(),
  last_run_from: z.union([z.string(), z.date()]).optional(),
  last_run_to: z.union([z.string(), z.date()]).optional(),
  scheduled_from: z.union([z.string(), z.date()]).optional(),
  scheduled_to: z.union([z.string(), z.date()]).optional(),
  last_updated_from: z.union([z.string(), z.date()]).optional(),
  last_updated_to: z.union([z.string(), z.date()]).optional(),
  is_playground: z.boolean().optional(),
  is_pending: z.boolean().optional(),
  is_latest_in_chain: z.boolean().optional(),
  ordering: ScheduleListOrdering.optional(),
});

/** Lowercase on the wire — the contract's prose spells it `AUTO`, the enum does not. */
const Granularity = z.enum(["auto", "hour", "day", "week", "month"]);

/**
 * The states behind an aggregate's `total`. No `cancelled`: the backend stopped counting
 * those here *and* in `total`, since a run stopped before a verdict dragged every pass rate
 * down. They still exist everywhere else — only the analytics rollups drop them.
 */
const CountsByState = z.object({
  success: z.number(),
  failure: z.number(),
  error: z.number(),
  warning: z.number(),
  anomalies: z.number(),
  skipped: z.number(),
});

/** Costs arrive as high-precision decimal strings; coerce so charts can add them. */
const DecimalAsNumber = z.union([z.number(), z.string()]).transform(Number);

const SparklinePoint = z.object({
  period: z.string(),
  failed: z.number(),
  total: z.number(),
});

const TimeseriesBucket = z.object({
  period: z.string(),
  total: z.number(),
  passed: z.number(),
  failed: z.number(),
  warning: z.number(),
  counts_by_state: CountsByState,
  pass_rate: RatioValue,
  total_duration_seconds: z.number(),
  average_duration_seconds: z.number(),
  total_cost: DecimalAsNumber,
  total_worker_cost: DecimalAsNumber,
  total_calculated_cost: DecimalAsNumber,
  // Optional, not just nullable: the contract leaves `currency` out of the bucket's
  // `required` list, so an omitted key would fail the whole timeseries parse.
  currency: z.string().nullable().optional(),
});

class SchedulesEndpoint {
  public static readonly basePath = "schedules";
  public static readonly schemas = {
    summary: {
      parameters: ScheduleListFilters,
      response: z.object({
        total_tests: z.number(),
        total_duration_seconds: z.number(),
        average_duration_seconds: z.number(),
        pass_rate: RatioValue,
        counts_by_state: CountsByState,
      }),
    },
    timeseries: {
      parameters: ScheduleListFilters.extend({
        granularity: Granularity.optional(),
        group_by: z.enum(["platform", "project", "worker"]).optional(),
      }),
      response: z.object({
        granularity: z.string(),
        group_by: z.string().nullable(),
        period_field: z.string(),
        date_from: z.string(),
        date_to: z.string(),
        buckets: TimeseriesBucket.array().nullable(),
        series: z.object({ key: z.string(), label: z.string(), buckets: TimeseriesBucket.array() }).array().nullable(),
        series_truncated: z.boolean(),
      }),
    },
    topFailing: {
      parameters: ScheduleListFilters.extend({
        granularity: Granularity.optional(),
        limit: z.number().int().min(1).max(50).optional(),
      }),
      response: z.object({
        granularity: z.string(),
        period_field: z.string(),
        date_from: z.string(),
        date_to: z.string(),
        limit: z.number(),
        suites: z
          .object({
            id: z.number(),
            name: z.string(),
            total: z.number(),
            failed: z.number(),
            pass_rate: RatioValue,
            counts_by_state: CountsByState,
            sparkline: SparklinePoint.array(),
          })
          .array(),
        cases: z
          .object({
            root_version_id: z.number(),
            key: z.string(),
            name: z.string(),
            // Optional as well as nullable: the contract leaves all three out of `required`,
            // so a case whose project was deleted would take the whole list down.
            project_id: z.number().nullable().optional(),
            project_name: z.string().nullable().optional(),
            platform: z.string().nullable().optional(),
            total: z.number(),
            failed: z.number(),
            pass_rate: RatioValue,
            counts_by_state: CountsByState,
            sparkline: SparklinePoint.array(),
          })
          .array(),
      }),
    },
    flaky: {
      parameters: ScheduleListFilters.extend({
        granularity: Granularity.optional(),
        limit: z.number().int().min(1).max(50).optional(),
        min_runs: z.number().int().min(2).max(100).optional(),
      }),
      response: z.object({
        granularity: z.string(),
        period_field: z.string(),
        date_from: z.string(),
        date_to: z.string(),
        limit: z.number(),
        min_runs: z.number(),
        cases: z
          .object({
            root_version_id: z.number(),
            key: z.string(),
            name: z.string(),
            // Optional as well as nullable: the contract leaves all three out of `required`,
            // so a case whose project was deleted would take the whole list down.
            project_id: z.number().nullable().optional(),
            project_name: z.string().nullable().optional(),
            platform: z.string().nullable().optional(),
            runs: z.number(),
            flips: z.number(),
            failed: z.number(),
            flakiness: RatioValue,
            sparkline: SparklinePoint.array(),
          })
          .array(),
      }),
    },
    listBatches: {
      parameters: ScheduleListFilters.extend({
        search: z.string().optional(),
        limit: z.number().int().positive().optional(),
        offset: z.number().int().min(0).optional(),
      }),
      response: z.object({
        count: z.number(),
        items: z
          .object({
            id: z.number(),
            created_at: DateTimeWithOffset,
            cancelled_at: NullishDateTimeWithOffset,
            /** When the first case actually ran; null while queued, since dispatch is not a start. */
            started_at: NullishDateTimeWithOffset,
            /** Processing time in seconds (finished_processing_at − started_processing_at). */
            time_taken: z.number().nullish(),
            name: z.string(),
            parent_schedule_id: z.number().nullable(),
            worker_id: z.number().nullable(),
            project_id: z.number(),
            project_name: z.string(),
            environment_id: z.number().nullish(),
            environment_name: z.string().nullish(),
            environment_url: z.string().nullish(),
            artifact_id: z.number().nullable().optional(),
            suits: z
              .object({
                id: z.number(),
                created_at: DateTimeWithOffset,
                name: z.string(),
                app_reset_mode: AppResetMode,
              })
              .array(),
            /** The batch's override; null leaves each suite to decide for itself. */
            app_reset_mode: AppResetMode.nullish(),
            /** What the worker was told: the override, else what the suites agree on,
             *  else `clear_data`. */
            effective_app_reset_mode: AppResetMode,
            max_steps: z.number().nullish(),
            max_turns_per_step: z.number().nullish(),
            schedule_time: DateTimeWithOffset,
            invocation_time: NullishDateTimeWithOffset,
            next_invokation_in_minutes: z.number(),
            allow_global_worker: z.boolean(),
            worker_name: z.string().nullable(),
            worker_version: z.string().nullish(),
            worker_commit_hash: z.string().nullish(),
            artifact_name: z.string().nullable(),
            is_running: z.boolean(),
            /** True until the occurrence is dispatched — the only marker of "will run". */
            is_pending: FalsyBool,
            is_playground: FalsyBool,
            last_run: NullishDateTimeWithOffset,
            updated_at: NullishDateTimeWithOffset,
            names_of_suites_in_batch: z.string().array(),
            names_of_test_cases_in_batch: z.string().array(),
            common_suite_name: z.string().nullable(),
            stats: z.object({
              total: z.number(),
              successful: z.number(),
              failed: z.number(),
              ratio: z.object({
                percentage: z.string(),
                value: z.number(),
              }),
            }),
            state_ratios: z
              .object({
                state: z.string(),
                ratio: z.number(),
              })
              .array(),
          })
          .and(UsageStatisticsSchema)
          .array(),
      }),
    },
    create: {
      parameters: z.object({
        project_id: z.string(),
        worker_id: z.union([z.string(), z.number()]),
        artifact_id: z.number(),
        /** Which of the project's environments the batch runs against — required for an API project
         *  even though the contract marks it optional. */
        environment_id: z.number().nullish(),
        parent_suite_id: z.number().nullable().optional(),
        create_from_suite_id: z.number().nullable().optional(),
      }),
      body: z.object({
        name: z.string(),
        schedule_time: z.union([z.number(), z.string()]).optional(),
        next_invokation_in_minutes: z.number().default(0),
        allow_global_worker: z.boolean().default(false),
        run_groups_on_single_worker: z.boolean().default(true),
        is_playground: z.boolean().optional(),
        /** Overrides what the runner does to the app between this batch's cases.
         *  Left out, each suite in the batch decides for its own. */
        app_reset_mode: AppResetMode.nullish(),
        /** Most steps the worker may take on one case of this batch. */
        max_steps: StepLimitIn.optional(),
        /** Most agent turns the worker may spend on one step. */
        max_turns_per_step: StepLimitIn.optional(),
        cases: z.number().array().nullable(),
      }),
      response: z.object({
        id: z.number(),
        created_at: DateTimeWithOffset,
        name: z.string(),
        schedule_time: NullishDateTimeWithOffset,
        invocation_time: NullishDateTimeWithOffset,
        next_invokation_in_minutes: z.number().nullish(),
        allow_global_worker: z.boolean().nullish(),
      }),
    },
    read: {
      response: z
        .object({
          id: z.number(),
          created_at: DateTimeWithOffset,
          updated_at: NullishDateTimeWithOffset,
          time_taken: z.number().nullish(),
          name: z.string(),
          parent_schedule_id: z.number().nullish(),
          worker_id: z.number().nullish(),
          worker_name: z.string().nullable().optional(),
          project_id: z.number(),
          project_name: z.string().nullable().optional(),
          artifact_id: z.number().nullable().optional(),
          artifact_name: z.string().nullable().optional(),
          /** API projects only; the backend keeps no copy of it, so its URL is whatever it is now. */
          environment_id: z.number().nullish(),
          environment_name: z.string().nullish(),
          environment_url: z.string().nullish(),
          schedule_time: DateTimeWithOffset,
          invocation_time: NullishDateTimeWithOffset,
          cancelled_at: NullishDateTimeWithOffset,
          next_invokation_in_minutes: z.number(),
          allow_global_worker: z.boolean(),
          cycles: z
            .object({
              id: z.number(),
              created_at: DateTimeWithOffset,
              started_processing_at: NullishDateTimeWithOffset,
              finished_processing_at: NullishDateTimeWithOffset,
              cancelled_at: NullishDateTimeWithOffset.optional(),
              removed_payload_at: NullishDateTimeWithOffset,
              worker_id: z.number().nullish(),
              worker_name: z.string().nullable().optional(),
              artifact_id: z.number().nullable().optional(),
              artifact_name: z.string().nullable().optional(),
              case: z
                .object({
                  id: z.number(),
                  created_at: DateTimeWithOffset,
                  name: z.string(),
                  payload: CasePayload,
                  version: z.number(),
                  project_id: z.number(),
                })
                .nullable(),
              schedule_id: z.number().nullable().optional(),
              state: z.string(),
              time_taken_in_seconds: z.number().nullable().optional(),
              phases: z
                .object({
                  id: z.number().nullable().optional(),
                  type: z.string().nullable().optional(),
                  state: z.string().nullable().optional(),
                  error: z
                    .object({
                      type: z.string().nullable().optional(),
                      message: z.string().nullable().optional(),
                    })
                    .nullable()
                    .optional(),
                })
                .array()
                .optional(),
            })
            .array()
            .nullable(),
          suits: z
            .object({
              id: z.number(),
              created_at: DateTimeWithOffset,
              name: z.string(),
              app_reset_mode: AppResetMode,
            })
            .array()
            .nullable(),
          /** The batch's override; null leaves each suite to decide for itself. */
          app_reset_mode: AppResetMode.nullish(),
          /** What the worker was told: the override, else what the suites agree on,
           *  else `clear_data`. */
          effective_app_reset_mode: AppResetMode,
          max_steps: z.number().nullish(),
          max_turns_per_step: z.number().nullish(),
          stats: z
            .object({
              total: z.number(),
              successful: z.number(),
              failed: z.number(),
              ratio: z.object({
                percentage: z.string(),
                value: z.number(),
              }),
            })
            .nullable()
            .optional(),
          state_ratios: z
            .object({
              state: z.string(),
              ratio: z.number(),
            })
            .array()
            .optional(),
          stream_data: StreamDataSchema.nullable().optional(),
        })
        .and(UsageStatisticsSchema),
    },
    /**
     * PUT is a full replace of `ScheduleIn` — there is no PATCH. A field left out goes back
     * to its default, which for the batch's overrides (`app_reset_mode`, the step limits) is
     * `null` — so those are required here and travel back from the row. And
     * `run_groups_on_single_worker` cannot be round-tripped (required here, returned by no
     * GET; callers send `true`, what `create` writes — reported to BE), and an update cannot
     * move a schedule to another device, the worker and artifact being POST query params.
     */
    update: {
      body: z.object({
        name: z.string(),
        schedule_time: z.string().optional(),
        next_invokation_in_minutes: z.number().optional(),
        allow_global_worker: z.boolean(),
        run_groups_on_single_worker: z.boolean(),
        app_reset_mode: AppResetMode.nullable(),
        max_steps: StepLimitIn,
        max_turns_per_step: StepLimitIn,
        is_playground: z.boolean().optional(),
        cases: z.number().array().nullish(),
      }),
    },
    /**
     * `schedules/{id}/review` — the batch-wide side of `cycles/{id}/review`. The cycles inside
     * `read` carry no override flag, so this is where the batch table learns which rows a person
     * overruled.
     */
    review: {
      response: z.object({
        batch_id: z.number(),
        overridden_count: z.number(),
        overridden_cycle_ids: z.number().array(),
        /** Only on the undo call: how many cycles it put back. */
        reverted_count: z.number().nullish(),
      }),
    },
  };

  constructor(private api: Auth) {}

  public async listBatches(
    query?: z.input<typeof SchedulesEndpoint.schemas.listBatches.parameters>,
  ): ActionReturnType<z.infer<typeof SchedulesEndpoint.schemas.listBatches.response>> {
    const params = validateInput(SchedulesEndpoint.schemas.listBatches.parameters, query ?? {});
    if (!params.ok) return params;
    return request({
      log: ["Schedules", "listBatches"],
      method: "get",
      path: [SchedulesEndpoint.basePath, "list"],
      query: params.data,
      response: SchedulesEndpoint.schemas.listBatches.response,
    });
  }

  public async create(
    params: z.input<typeof SchedulesEndpoint.schemas.create.parameters>,
    body: z.input<typeof SchedulesEndpoint.schemas.create.body>,
  ): ActionReturnType<z.infer<typeof SchedulesEndpoint.schemas.create.response>> {
    const queryValidated = validateInput(SchedulesEndpoint.schemas.create.parameters, params);
    if (!queryValidated.ok) return queryValidated;
    const bodyValidated = validateInput(SchedulesEndpoint.schemas.create.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Schedules", "create"],
      method: "post",
      path: [SchedulesEndpoint.basePath],
      query: queryValidated.data,
      json: bodyValidated.data,
      response: SchedulesEndpoint.schemas.create.response,
    });
  }

  public read(id: string | number): ActionReturnType<z.infer<typeof SchedulesEndpoint.schemas.read.response>> {
    return request({
      log: ["Schedules", "read"],
      method: "get",
      path: [SchedulesEndpoint.basePath, id],
      response: SchedulesEndpoint.schemas.read.response,
    });
  }

  public async update(
    id: string | number,
    body: z.input<typeof SchedulesEndpoint.schemas.update.body>,
  ): ActionReturnType<undefined> {
    const bodyValidated = validateInput(SchedulesEndpoint.schemas.update.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Schedules", "update"],
      method: "put",
      path: [SchedulesEndpoint.basePath, id],
      json: bodyValidated.data,
    });
  }

  public delete(id: string): ActionReturnType<undefined> {
    return request({
      log: ["Schedules", "delete"],
      method: "delete",
      path: [SchedulesEndpoint.basePath, id],
    });
  }

  public addSuiteToSchedule(scheduleId: string | number, suiteId: string | number): ActionReturnType<undefined> {
    return request({
      log: ["Schedules", "addSuiteToSchedule"],
      method: "post",
      path: [SchedulesEndpoint.basePath, scheduleId, suiteId],
    });
  }

  public removeSuiteFromSchedule(scheduleId: string, suiteId: string): ActionReturnType<undefined> {
    return request({
      log: ["Schedules", "removeSuiteFromSchedule"],
      method: "delete",
      path: [SchedulesEndpoint.basePath, scheduleId, suiteId],
    });
  }

  public cancel(id: string | number): ActionReturnType<undefined> {
    return request({
      log: ["Schedules", "cancel"],
      method: "post",
      path: [SchedulesEndpoint.basePath, id, "cancel"],
    });
  }

  public review(id: string | number): ActionReturnType<BatchReview> {
    return request({
      log: ["Schedules", "review"],
      method: "get",
      path: [SchedulesEndpoint.basePath, id, "review"],
      stripTrailingSlash: true,
      response: SchedulesEndpoint.schemas.review.response,
    });
  }

  /** Every decision made on the batch at once — each cycle goes back to what its run said. */
  public reviewRevoke(id: string | number): ActionReturnType<BatchReview> {
    return request({
      log: ["Schedules", "reviewRevoke"],
      method: "post",
      path: [SchedulesEndpoint.basePath, id, "review", "revoke"],
      stripTrailingSlash: true,
      response: SchedulesEndpoint.schemas.review.response,
    });
  }

  public async summary(
    query?: z.input<typeof SchedulesEndpoint.schemas.summary.parameters>,
  ): ActionReturnType<z.infer<typeof SchedulesEndpoint.schemas.summary.response>> {
    const params = validateInput(SchedulesEndpoint.schemas.summary.parameters, query ?? {});
    if (!params.ok) return params;
    return request({
      log: ["Schedules", "summary"],
      method: "get",
      path: [SchedulesEndpoint.basePath, "summary"],
      query: params.data,
      response: SchedulesEndpoint.schemas.summary.response,
    });
  }

  public async timeseries(
    query?: z.input<typeof SchedulesEndpoint.schemas.timeseries.parameters>,
  ): ActionReturnType<z.infer<typeof SchedulesEndpoint.schemas.timeseries.response>> {
    const params = validateInput(SchedulesEndpoint.schemas.timeseries.parameters, query ?? {});
    if (!params.ok) return params;
    return request({
      log: ["Schedules", "timeseries"],
      method: "get",
      path: [SchedulesEndpoint.basePath, "timeseries"],
      query: params.data,
      response: SchedulesEndpoint.schemas.timeseries.response,
    });
  }

  public async topFailing(
    query?: z.input<typeof SchedulesEndpoint.schemas.topFailing.parameters>,
  ): ActionReturnType<z.infer<typeof SchedulesEndpoint.schemas.topFailing.response>> {
    const params = validateInput(SchedulesEndpoint.schemas.topFailing.parameters, query ?? {});
    if (!params.ok) return params;
    return request({
      log: ["Schedules", "topFailing"],
      method: "get",
      path: [SchedulesEndpoint.basePath, "top-failing"],
      query: params.data,
      response: SchedulesEndpoint.schemas.topFailing.response,
    });
  }

  public async flaky(
    query?: z.input<typeof SchedulesEndpoint.schemas.flaky.parameters>,
  ): ActionReturnType<z.infer<typeof SchedulesEndpoint.schemas.flaky.response>> {
    const params = validateInput(SchedulesEndpoint.schemas.flaky.parameters, query ?? {});
    if (!params.ok) return params;
    return request({
      log: ["Schedules", "flaky"],
      method: "get",
      path: [SchedulesEndpoint.basePath, "flaky"],
      query: params.data,
      response: SchedulesEndpoint.schemas.flaky.response,
    });
  }
}

/**
 * Exported as types only — the schemas stay module-private, so this endpoint remains the one
 * place a wire payload is parsed, while `lib/view/analytics.ts` stays pure and testable.
 */
export type TimeseriesBucket = z.infer<typeof TimeseriesBucket>;
export type TimeseriesSeries = z.infer<typeof SchedulesEndpoint.schemas.timeseries.response>["series"];
export type BatchSummary = z.infer<typeof SchedulesEndpoint.schemas.summary.response>;
export type TopFailingSuite = z.infer<typeof SchedulesEndpoint.schemas.topFailing.response>["suites"][number];
export type TopFailingCase = z.infer<typeof SchedulesEndpoint.schemas.topFailing.response>["cases"][number];
export type FlakyCase = z.infer<typeof SchedulesEndpoint.schemas.flaky.response>["cases"][number];
export type SparklinePoint = z.infer<typeof SparklinePoint>;
export type BatchReview = z.infer<typeof SchedulesEndpoint.schemas.review.response>;

export { SchedulesEndpoint };
